import { fetchQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { apiQueryClient } from 'features/api/apiQueryClient'

/**
 * Warms the cache with an automation execution so its execution summary renders
 * without an additional fetch when the user opens it. Used as the `onPreload`
 * for automation execution list items; the returned promise lets the item defer
 * its transition until the data is ready.
 *
 * The summary reads its reviewable tasks from the admin endpoint when admin
 * view is on, so pass `adminView` to warm whichever query it will actually
 * read (see useShouldUseAdminManualTasks).
 */
export const prefetchExecutionSummary = ({
  executionId,
  manualTaskId,
  adminView = false
}: {
  executionId: string
  manualTaskId?: string
  adminView?: boolean
}): Promise<unknown> => {
  const manualTasksByExecution = adminView
    ? fetchQuery(apiQueryClient, dashboardv99990101Client.getAdminManualTasks, {
        executionId,
        limit: 100
      })
    : fetchQuery(apiQueryClient, dashboardv99990101Client.getManualTasks, {
        executionId,
        limit: 100
      })

  return Promise.all([
    fetchQuery(
      apiQueryClient,
      dashboardv99990101Client.getAutomationExecution,
      {
        id: executionId
      }
    ),
    manualTasksByExecution,
    manualTaskId
      ? fetchQuery(apiQueryClient, dashboardv99990101Client.getManualTask, {
          id: manualTaskId
        })
      : Promise.resolve()
  ])
}

/**
 * Warms the cache with a legacy workflow, whose preview is the legacy
 * equivalent of an execution summary.
 */
export const prefetchLegacyWorkflowSummary = (
  workflowId: string
): Promise<unknown> =>
  fetchQuery(apiQueryClient, dashboardv99990101Client.getWorkflow, {
    workflowId
  })
